import {
  modelInputProps,
  modelScaleProps,
  queryEraseModelProps,
  Sam3PredictionResponse,
  Sam3SessionDescriptor,
  setParmsandQueryEraseModelProps,
  setParmsandQueryModelProps,
} from "./Interface";

const API_ENDPOINT = "/segment_everything_box_model";
const ERASE_API_ENDPOINT = process.env.ERASE_API_ENDPOINT;

type Sam3Point = { x: number; y: number; label: 0 | 1 };

export interface Sam3PromptRequest {
  session_id: string;
  points?: Sam3Point[];
  box?: [number, number, number, number];
  previous_prediction_id: string | null;
  multimask_output: boolean;
}

const responseError = async (response: Response) => {
  try {
    const payload = await response.json();
    return payload?.error?.message || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
};

export const decodeSessionDescriptor = (encoded: string): Sam3SessionDescriptor => {
  const descriptor = JSON.parse(window.atob(encoded));
  if (
    descriptor?.version !== 3 ||
    typeof descriptor.session_id !== "string" ||
    !descriptor.session_id ||
    !Number.isInteger(descriptor.width) ||
    !Number.isInteger(descriptor.height) ||
    descriptor.width < 1 ||
    descriptor.height < 1
  ) {
    throw new Error("Invalid SAM3 session descriptor");
  }
  return descriptor as Sam3SessionDescriptor;
};

const querySam3Session = async (blob: Blob): Promise<Sam3SessionDescriptor> => {
  const response = await fetch(API_ENDPOINT, { method: "POST", body: blob });
  if (!response.ok) throw new Error(await responseError(response));
  const payload = await response.json();
  if (!Array.isArray(payload) || typeof payload[0] !== "string") {
    throw new Error("Invalid SAM3 session response");
  }
  return decodeSessionDescriptor(payload[0]);
};

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Unable to encode image"))),
      "image/jpeg",
      1.0
    );
  });

const setParmsandQueryModel = async ({
  width,
  height,
  uploadScale,
  imgData,
  handleSegModelResults,
}: setParmsandQueryModelProps) => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * uploadScale);
  canvas.height = Math.round(height * uploadScale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is unavailable");
  context.drawImage(imgData, 0, 0, canvas.width, canvas.height);
  const session = await querySam3Session(await canvasToBlob(canvas));
  handleSegModelResults({ session });
};

export const buildSam3PromptRequest = ({
  sessionId,
  clicks,
  modelScale,
  previousPredictionId,
  multimaskOutput,
}: {
  sessionId: string;
  clicks: modelInputProps[];
  modelScale: modelScaleProps;
  previousPredictionId: string | null;
  multimaskOutput: boolean;
}): Sam3PromptRequest => {
  const coordinateScale = modelScale.onnxScale;
  if (!Number.isFinite(coordinateScale) || coordinateScale <= 0) {
    throw new Error("Invalid image coordinate scale");
  }
  const first = clicks[0];
  const hasBox =
    first?.clickType === 2 && first.width !== null && first.height !== null;
  const pointClicks = hasBox ? clicks.slice(1) : clicks;
  const points = pointClicks
    .filter((click) => click.clickType === 0 || click.clickType === 1)
    .map((click) => ({
      x: click.x / coordinateScale,
      y: click.y / coordinateScale,
      label: click.clickType as 0 | 1,
    }));
  const promptCount = points.length + (hasBox ? 1 : 0);
  const request: Sam3PromptRequest = {
    session_id: sessionId,
    previous_prediction_id:
      promptCount > 1 ? previousPredictionId : null,
    multimask_output: multimaskOutput,
  };
  if (points.length) request.points = points;
  if (hasBox) {
    request.box = [
      first.x / coordinateScale,
      first.y / coordinateScale,
      first.width! / coordinateScale,
      first.height! / coordinateScale,
    ];
  }
  if (!request.points && !request.box) {
    throw new Error("At least one point or box is required");
  }
  return request;
};

export const querySam3Prediction = async (
  payload: Sam3PromptRequest,
  signal?: AbortSignal
): Promise<Sam3PredictionResponse> => {
  const response = await fetch(`${API_ENDPOINT}/predict`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) throw new Error(await responseError(response));
  const prediction = (await response.json()) as Sam3PredictionResponse;
  if (
    typeof prediction.prediction_id !== "string" ||
    !Number.isInteger(prediction.width) ||
    !Number.isInteger(prediction.height) ||
    !Array.isArray(prediction.masks) ||
    !prediction.masks.length
  ) {
    throw new Error("Invalid SAM3 prediction response");
  }
  return prediction;
};

export const decodeMaskPng = (
  pngBase64: string,
  expectedWidth: number,
  expectedHeight: number
): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth !== expectedWidth || image.naturalHeight !== expectedHeight) {
        reject(new Error("SAM3 mask dimensions do not match the image session"));
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = expectedWidth;
      canvas.height = expectedHeight;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        reject(new Error("Canvas is unavailable"));
        return;
      }
      context.drawImage(image, 0, 0);
      const rgba = context.getImageData(0, 0, expectedWidth, expectedHeight).data;
      const mask = new Uint8Array(expectedWidth * expectedHeight);
      for (let index = 0; index < mask.length; index += 1) {
        mask[index] = rgba[index * 4 + 3];
      }
      resolve(mask);
    };
    image.onerror = () => reject(new Error("Unable to decode SAM3 mask PNG"));
    image.src = `data:image/png;base64,${pngBase64}`;
  });

export const deleteSam3Session = async (sessionId: string) => {
  try {
    await fetch(`${API_ENDPOINT}/session/${encodeURIComponent(sessionId)}`, {
      method: "DELETE",
      keepalive: true,
    });
  } catch {
    // Session TTL remains the fallback when the iframe disappears abruptly.
  }
};

const queryEraseModel = async ({
  image,
  mask,
  handlePredictedImage,
}: queryEraseModelProps) => {
  if (!ERASE_API_ENDPOINT) throw new Error("ERASE_API_ENDPOINT is not configured");
  const eraseResponse = await fetch(ERASE_API_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      image,
      mask: Array.from(mask as ArrayLike<number>),
      dilate_kernel_size: 24,
    }),
  });
  if (!eraseResponse.ok) throw new Error(await responseError(eraseResponse));
  const eraseResult = await eraseResponse.text();
  handlePredictedImage(`data:image/png;base64, ${eraseResult}`);
};

const getBase64StringFromDataURL = (dataURL: string) =>
  dataURL.replace("data:", "").replace(/^.+,/, "");

const setParmsandQueryEraseModel = ({
  width,
  height,
  uploadScale,
  imgData,
  mask,
  handlePredictedImage,
}: setParmsandQueryEraseModelProps) => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * uploadScale);
  canvas.height = Math.round(height * uploadScale);
  const context = canvas.getContext("2d");
  if (!context) return;
  context.drawImage(imgData || new Image(), 0, 0, canvas.width, canvas.height);
  queryEraseModel({
    image: getBase64StringFromDataURL(canvas.toDataURL()),
    mask,
    handlePredictedImage,
  });
};

export { setParmsandQueryModel, setParmsandQueryEraseModel };
